// packages/web-ui/src/components/badges/ActivityBarBadge.tsx
//
// W2-S3 Task 2 (AC #4) — status pill rendered at top-right of an ActivityBar
// destination button. Three variants (numeric / warning-dot / error-dot).
// Pure presentation: data flows ONE WAY (slice → selector → this component).
// Source of truth: UX §4.5 (badge spec) + AC #4 of story 2-3.

import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useReducedMotion } from '../../AppShell/useReducedMotion.js';

/** Badge variant — `numeric` displays a count, `warning`/`error` are dots. */
export type ActivityBarBadgeKind = 'numeric' | 'warning' | 'error';

export interface ActivityBarBadgeProps {
  kind: ActivityBarBadgeKind;
  /** Required for `numeric`; ignored for `warning`/`error`. */
  count?: number;
}

/**
 * Renders a 14 px numeric circle or 8 px warning/error dot at top-right of the
 * parent button. Numeric variant hides at `count === 0`. Counts > 9 truncate
 * to `"9+"`. Fades in/out with a 150 ms transition unless the user prefers
 * reduced motion.
 *
 * Decorative — `aria-hidden="true"`. The button's own `aria-label` is augmented
 * by `<ActivityBar>` to reflect badge state for screen-reader users.
 */
export function ActivityBarBadge({ kind, count = 0 }: ActivityBarBadgeProps): JSX.Element | null {
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(false);

  // Hide the numeric badge entirely when count is zero — no fade either.
  const shouldRender = kind !== 'numeric' || count > 0;

  // Trigger the fade-in on mount (or whenever the data conditions flip on).
  useEffect(() => {
    if (!shouldRender) {
      setVisible(false);
      return;
    }
    if (reduced) {
      setVisible(true);
      return;
    }
    // Force a frame so the opacity transition triggers (RTL renders synchronously
    // in jsdom — using `setTimeout(0)` is sufficient and well-supported).
    const handle = window.setTimeout(() => {
      setVisible(true);
    }, 0);
    return () => {
      window.clearTimeout(handle);
    };
  }, [reduced, shouldRender]);

  if (!shouldRender) return null;

  const baseClass =
    'absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 pointer-events-none';
  const transitionClass = reduced
    ? 'opacity-100'
    : `transition-opacity duration-150 ${visible ? 'opacity-100' : 'opacity-0'}`;

  if (kind === 'numeric') {
    const display = count > 9 ? '9+' : String(count);
    return (
      <span
        aria-hidden="true"
        data-testid="activity-bar-badge-numeric"
        data-count={String(count)}
        className={`${baseClass} ${transitionClass} flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-accent-red px-[3px] text-[9px] font-semibold leading-none text-white`}
      >
        {display}
      </span>
    );
  }

  // Dot variants — 8 px circle.
  const dotColour = kind === 'error' ? 'bg-accent-red' : 'bg-accent-amber';
  return (
    <span
      aria-hidden="true"
      data-testid={`activity-bar-badge-${kind}`}
      className={`${baseClass} ${transitionClass} block h-2 w-2 rounded-full ${dotColour}`}
    />
  );
}
